import { useState, type FormEvent } from "react";
import { FileSpreadsheet } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useParams, useSearchParams } from "react-router-dom";

import type {
  CoaAction,
  CoaDiagnosticItem,
  CoaDiagnosticQuery,
  CoaStatus,
} from "@/api/diagnostic-chart-of-accounts";
import type { DiagnosticTabProps } from "@/components/projects/diagnostic/DiagnosticSection";
import {
  COA_ACTIONS,
  COA_STATUSES,
  finalAccount,
} from "@/components/projects/diagnostic/selectors/chartOfAccounts";
import { NO_VALUE } from "@/components/projects/diagnostic/sectionState";
import { useProject } from "@/hooks/useApi";
import { useCurrentUser } from "@/hooks/useAuth";
import { useDiagnosticChartOfAccounts } from "@/hooks/useDiagnosticChartOfAccounts";
import { formatCount } from "@/lib/format";
import { hasMinRole } from "@/lib/roles";
import { cn } from "@/lib/utils";

import { CoaAnalysisActions } from "./CoaAnalysisActions";
import { CoaStatusBadge } from "./CoaStatusBadge";
import { useCoaActionLabels, useCoaStatusLabels } from "./coaLabels";
import { downloadChartOfAccountsWorkbook } from "./exportChartOfAccounts";
import { YcoaWithoutClientSection } from "./YcoaWithoutClientSection";

const PAGE_SIZE = 50;

function readQuery(params: URLSearchParams): CoaDiagnosticQuery {
  const status = params.get("status");
  const decision = params.get("decision");
  const page = Number(params.get("page"));
  return {
    status: (COA_STATUSES as readonly string[]).includes(status ?? "")
      ? (status as CoaStatus)
      : undefined,
    decision: (COA_ACTIONS as readonly string[]).includes(decision ?? "")
      ? (decision as CoaAction)
      : undefined,
    q: params.get("q") || undefined,
    page: Number.isInteger(page) && page > 1 ? page : 1,
    pageSize: PAGE_SIZE,
  };
}

/**
 * O drill-down do plano de contas (#1374 P2, EF-FUNC-009 §8.1): a lista resumida
 * da decisão, para apresentar ao cliente.
 *
 * O filtro mora na URL: o quadro do Ato 1 abre já filtrado pelo status ou pela
 * ação clicada, e o link colado abre igual. A lista é a segunda leitura do
 * Diagnóstico, como a árvore da estrutura organizacional; o quadro traz só o
 * agregado.
 */
export function ChartOfAccountsDetail(_props: DiagnosticTabProps) {
  const { t } = useTranslation();
  const { projectId } = useParams<{ projectId: string }>();
  const [params, setParams] = useSearchParams();
  const query = readQuery(params);
  const { data, isLoading, refetch } = useDiagnosticChartOfAccounts(projectId, query);
  const statusLabels = useCoaStatusLabels();
  const actionLabels = useCoaActionLabels();
  const [search, setSearch] = useState(query.q ?? "");
  const [exporting, setExporting] = useState(false);
  // O nome vai no arquivo; a página já leu o projeto, então vem do cache.
  const { data: project } = useProject(projectId);
  const isConsultant = hasMinRole(useCurrentUser()?.role, "consultant");

  // #1715, cartão 7.6: a lista resumida do 8.1, com os filtros da tela, para
  // qualquer usuário do projeto. Ocupado, o botão não baixa duas vezes.
  async function exportList() {
    if (!projectId) return;
    setExporting(true);
    try {
      await downloadChartOfAccountsWorkbook(
        projectId,
        { status: query.status, decision: query.decision, q: query.q },
        t,
        { status: statusLabels, action: actionLabels },
        project?.name,
      );
    } finally {
      setExporting(false);
    }
  }

  function update(next: Partial<Record<"status" | "decision" | "q", string | undefined>>) {
    const merged = new URLSearchParams(params);
    for (const [key, value] of Object.entries(next)) {
      if (value) merged.set(key, value);
      else merged.delete(key);
    }
    // Filtro novo volta à primeira página.
    merged.delete("page");
    setParams(merged, { replace: true });
  }

  function goTo(page: number) {
    const merged = new URLSearchParams(params);
    if (page > 1) merged.set("page", String(page));
    else merged.delete("page");
    setParams(merged, { replace: true });
  }

  function submitSearch(event: FormEvent) {
    event.preventDefault();
    update({ q: search.trim() || undefined });
  }

  if (isLoading && !data) {
    return <p className="text-[13px] text-[#596b85]">{t("Loading…")}</p>;
  }

  if (!data) {
    return (
      <div
        data-testid="coa-detail-error"
        className="flex flex-wrap items-center gap-3 rounded-lg border border-[#f3c7c2] bg-[#fdf3f2] px-4 py-3 text-[13px] text-[#a23b2f]"
      >
        {t("Could not load the chart of accounts.")}
        <button
          type="button"
          onClick={() => void refetch()}
          className="rounded-md border border-current px-3 py-1 text-[12px] font-semibold"
        >
          {t("Try again")}
        </button>
      </div>
    );
  }

  const page = data.page;
  const from = data.total === 0 ? 0 : (page - 1) * data.page_size + 1;
  const to = Math.min(page * data.page_size, data.total);
  const filtered = !!(query.status || query.decision || query.q);

  return (
    <div className="space-y-4">
      {projectId && <CoaAnalysisActions projectId={projectId} filters={query} />}
      {projectId && isConsultant && <YcoaWithoutClientSection projectId={projectId} />}
      <p className="max-w-3xl text-[13px] leading-relaxed text-[#596b85]">
        {t(
          "Each client account with its adherence status and the action for the YCOA. Where the consultant has not decided yet, the action is the suggested one.",
        )}
      </p>

      <div className="flex flex-wrap gap-2" role="group" aria-label={t("Filter by status")}>
        <FilterButton active={!query.status} onClick={() => update({ status: undefined })}>
          {t("All statuses")}
        </FilterButton>
        {COA_STATUSES.map((status) => (
          <FilterButton
            key={status}
            active={query.status === status}
            onClick={() => update({ status })}
          >
            {`${status} · ${statusLabels[status]}`}
          </FilterButton>
        ))}
      </div>

      <div className="flex flex-wrap gap-2" role="group" aria-label={t("Filter by action")}>
        <FilterButton active={!query.decision} onClick={() => update({ decision: undefined })}>
          {t("All actions")}
        </FilterButton>
        {COA_ACTIONS.map((action) => (
          <FilterButton
            key={action}
            active={query.decision === action}
            onClick={() => update({ decision: action })}
          >
            {actionLabels[action]}
          </FilterButton>
        ))}
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <form onSubmit={submitSearch} className="flex min-w-0 max-w-md flex-1 gap-2">
          <input
            type="search"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder={t("Account or description")}
            aria-label={t("Search accounts")}
            className="min-w-0 flex-1 rounded-md border border-[#d4dae3] bg-white px-3 py-1.5 text-[13px] text-[#08254c] placeholder:text-[#9aa5b5] focus:outline-none focus:ring-2 focus:ring-[#08254c]"
          />
          <button
            type="submit"
            className="rounded-md border border-[#d4dae3] px-3 py-1.5 text-[12px] font-semibold text-[#08254c] hover:bg-[#f5f8fc]"
          >
            {t("Search")}
          </button>
        </form>
        <button
          type="button"
          onClick={() => void exportList()}
          disabled={data.total === 0 || exporting}
          className="ml-auto inline-flex items-center gap-1.5 rounded-md border border-[#d7dde6] bg-white px-3 py-1.5 text-[12px] font-semibold text-[#08254c] hover:bg-[#f5f8fc] disabled:cursor-not-allowed disabled:opacity-50"
        >
          <FileSpreadsheet aria-hidden className="h-3.5 w-3.5" />
          {t("Download list (.xlsx)")}
        </button>
      </div>

      {data.items.length === 0 ? (
        <div
          data-testid="coa-detail-empty"
          className="rounded-lg border border-dashed border-[#d4dae3] px-4 py-6 text-[13px] text-[#596b85]"
        >
          {filtered
            ? t("No account matches the filter.")
            : t("The project has no chart of accounts analysis yet.")}
        </div>
      ) : (
        // A tabela rola dentro do quadro: a página não rola na horizontal no celular.
        <div className="overflow-x-auto rounded-lg border border-[#e8ebf0] bg-white">
          <table className="w-full min-w-[880px] text-left text-[12px]">
            <thead className="border-b border-[#e8ebf0] font-mono text-[10px] uppercase tracking-[0.08em] text-[#7b8799]">
              <tr>
                <th className="px-3 py-2 font-medium">{t("Client account")}</th>
                <th className="px-3 py-2 font-medium">{t("Description")}</th>
                <th className="px-3 py-2 font-medium">{t("Status")}</th>
                <th className="px-3 py-2 font-medium">{t("Action")}</th>
                <th className="px-3 py-2 font-medium">{t("Mirror account")}</th>
                <th className="px-3 py-2 font-medium">{t("Final account in YCOA")}</th>
                <th className="px-3 py-2 font-medium">{t("Consultant note")}</th>
              </tr>
            </thead>
            <tbody>
              {data.items.map((item) => (
                <AccountRow
                  key={item.account}
                  item={item}
                  statusLabel={statusLabels[item.status]}
                  actionLabel={actionLabels[item.effective_decision]}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}

      {data.total > 0 && (
        <div className="flex flex-wrap items-center justify-between gap-2 text-[12px] text-[#596b85]">
          <span>
            {t("{{from}}-{{to}} of {{total}} accounts", {
              from: formatCount(from),
              to: formatCount(to),
              total: formatCount(data.total),
            })}
          </span>
          <div className="flex gap-2">
            <FilterButton active={false} disabled={page <= 1} onClick={() => goTo(page - 1)}>
              {t("Previous page")}
            </FilterButton>
            <FilterButton active={false} disabled={to >= data.total} onClick={() => goTo(page + 1)}>
              {t("Next page")}
            </FilterButton>
          </div>
        </div>
      )}
    </div>
  );
}

function AccountRow({
  item,
  statusLabel,
  actionLabel,
}: {
  item: CoaDiagnosticItem;
  statusLabel: string;
  actionLabel: string;
}) {
  const { t } = useTranslation();
  const final = finalAccount(item);
  return (
    <tr data-testid={`coa-row-${item.account}`} className="border-b border-[#f2f4f7] align-top">
      <td className="px-3 py-2 font-mono tabular-nums text-[#08254c]">{item.account}</td>
      <td className="px-3 py-2 text-[#08254c]">{item.description ?? NO_VALUE}</td>
      <td className="px-3 py-2">
        <span className="inline-flex items-center gap-2 text-[#08254c]">
          <CoaStatusBadge status={item.status} />
          {statusLabel}
        </span>
      </td>
      <td className="px-3 py-2 text-[#08254c]">
        {actionLabel}
        {item.decision == null && (
          <span className="ml-2 rounded-sm border border-[#d4dae3] px-1.5 py-0.5 text-[10px] text-[#596b85]">
            {t("Suggested")}
          </span>
        )}
      </td>
      <td className="px-3 py-2 font-mono tabular-nums text-[#08254c]">
        {item.effective_mirror_account ?? NO_VALUE}
      </td>
      <td data-testid="coa-final" className="px-3 py-2 font-mono tabular-nums text-[#08254c]">
        {final.kind === "same_as_ycoa"
          ? t("Same as YCOA ({{account}})", { account: final.account })
          : final.kind === "account"
            ? final.account
            : NO_VALUE}
      </td>
      <td className="px-3 py-2 text-[#596b85]">{item.note ?? ""}</td>
    </tr>
  );
}

function FilterButton({
  active,
  disabled = false,
  onClick,
  children,
}: {
  active: boolean;
  disabled?: boolean;
  onClick: () => void;
  children: string;
}) {
  return (
    <button
      type="button"
      aria-pressed={active}
      disabled={disabled}
      onClick={onClick}
      className={cn(
        "rounded-full border px-3 py-1 text-[12px] transition-colors disabled:cursor-not-allowed disabled:opacity-40",
        active
          ? "border-[#08254c] bg-[#08254c] text-white"
          : "border-[#d4dae3] bg-white text-[#08254c] hover:bg-[#f5f8fc]",
      )}
    >
      {children}
    </button>
  );
}
